import React, { useState, useEffect } from 'react';
import LeadDetail from './LeadDetail';
import { tokens as tk } from './theme';
import { PhoneCall, RefreshCcw, FileCheck2, ClipboardCheck, CalendarClock, ChevronDown, ArrowRight, Sparkles, Phone } from 'lucide-react';

const API = 'https://tcio-api.onrender.com';

const DOT = '\u00B7';
const CHECK = '\u2713';
const ELLIP = '\u2026';
const TIMES = '\u2715';
const STAR = '\u2726';
const BULLET = '\u25CF';

const CSS = `
.tqRow { transition: background 160ms ease; }
.tqRow:hover { background: ${tk.surfaceSoft}; }
.tqRowOpen { background: ${tk.aiSoft} !important; }
.tqChev { transition: transform 180ms ease; }
.tqChevOpen { transform: rotate(180deg); }
.tqSeg { transition: background 150ms ease, color 150ms ease, border-color 150ms ease; }
.tqSeg:hover { border-color: ${tk.blue400}; color: ${tk.blue700}; background: ${tk.aiSoft}; }
.tqFix { transition: color 150ms ease; }
.tqFix:hover { color: ${tk.aiHover}; }
`;

const money = (n) => n ? '$' + Math.round(parseFloat(n)).toLocaleString() : '';
const payerGrade = (r) => {
  const v = parseFloat(r || 0);
  if (v >= 20) return { t: 'A', c: tk.success };
  if (v >= 10) return { t: 'B', c: tk.blue600 };
  if (v >= 5)  return { t: 'C', c: tk.warning };
  return { t: 'D', c: tk.danger };
};
const timeAt = (t) => new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const minsAgo = (t) => Math.max(0, Math.round((Date.now() - new Date(t).getTime()) / 60000));
const durShort = (m) => m < 60 ? Math.round(m) + 'm' : m < 1440 ? Math.round(m / 60) + 'h' : Math.round(m / 1440) + 'd';
const timeAgo = (t) => durShort(minsAgo(t)) + ' ago';
const trunc = (s, n) => s && s.length > n ? s.slice(0, n) + ELLIP : (s || '');
const initials = (f, l) => ((f || ' ')[0] + (l || ' ')[0]).toUpperCase();
const AV = [tk.blue600, tk.purple, tk.teal, tk.ai, tk.blue700, tk.success];
const avColor = (name) => AV[(((name || '').charCodeAt(0) || 0) + ((name || '').charCodeAt(1) || 0)) % AV.length];
const NV_REASONS = ['Not seeking treatment','Spam / robocall','Out of area','Wrong fit / LOC','Bystander / third party','AI misclassified','Other'];

function prioritize(l, bucketKey) {
  const now = Date.now();
  const dueMs = l.next_action_at ? new Date(l.next_action_at).getTime() : now;
  const overdueMin = Math.max(0, (now - dueMs) / 60000);

  if (l.crisis_flag && !l.crisis_resolved_at)
    return { tier: 0, score: 1e9 + overdueMin, why: 'Crisis concern detected on call \u2014 call immediately' };

  const vobMin = l.vob_at ? minsAgo(l.vob_at) : null;
  if (l.pipeline_stage === 'vob_completed' && vobMin !== null && vobMin < 24 * 60)
    return { tier: 1, score: 1e8 - vobMin, why: 'VOB back ' + (vobMin < 60 ? vobMin + ' min' : Math.round(vobMin / 60) + 'h') + ' ago \u2014 beat the other centers to them' };

  const ageMin = l.lead_created_at ? minsAgo(l.lead_created_at) : 1e9;
  if (l.pipeline_stage === 'new' && ageMin < 120)
    return { tier: 2, score: 1e7 - ageMin, why: 'Brand new \u2014 called ' + (ageMin < 60 ? ageMin + ' min' : Math.round(ageMin / 60) + 'h') + ' ago. Speed wins.' };

  if ((l.source_channel || '').toLowerCase().includes('alumni'))
    return { tier: 3, score: 1e6 + overdueMin, why: 'Alumni returning \u2014 treated here before. Warmest call you have.' };

  if (l.callback_at && new Date(l.callback_at).getTime() <= now + 15 * 60000)
    return { tier: 4, score: 1e5 + overdueMin, why: 'You promised ' + timeAt(l.callback_at) + ' \u2014 keep your word' };

  if (bucketKey === 'now')
    return { tier: 5, score: overdueMin, why: l.next_action_reason || 'Follow-up due' };

  return { tier: 6, score: -dueMs, why: l.next_action_reason || '' };
}

const TIER_NAMES = { 0: 'Crisis', 1: 'VOB back', 2: 'New lead', 3: 'Alumni', 4: 'Callback', 5: 'Follow-up', 6: 'Scheduled' };
const prioOf = (tier) => tier === 0
  ? { label: 'CRITICAL', c: tk.danger }
  : tier <= 2
    ? { label: 'HIGH', c: tk.danger }
    : tier <= 4
      ? { label: 'MEDIUM', c: tk.warning }
      : { label: 'LOW', c: tk.textMuted };

const taskOf = (l) => {
  if (l.pipeline_stage === 'pending_admit') return 'admit';
  if (l.pipeline_stage === 'prescreen_done') return 'prescreen';
  if (l.pipeline_stage === 'vob_pending' || l.pipeline_stage === 'vob_completed') return 'vob';
  if (l.callback_at) return 'callback';
  if (l.pipeline_stage === 'new') return 'call';
  return 'followup';
};
const TASK_LABELS = { call: 'First call', callback: 'Callback', followup: 'Follow up', vob: 'VOB follow-up', prescreen: 'Prescreen', admit: 'Confirm admit' };
const STAGE_LABELS = { new: 'Inquiry', contacted: 'Contacted', vob_pending: 'VOB Pending', vob_completed: 'VOB Complete', prescreen_done: 'Prescreen Done', pending_admit: 'Pending Admit' };
const stageDot = (st) => st === 'vob_completed' ? tk.success : st === 'pending_admit' ? tk.warning : st === 'vob_pending' ? tk.warning : tk.blue500;
const SPINE = ['Inquiry', 'Contacted', 'VOB', 'Prescreen', 'Pending Admit', 'Admitted'];
const STEP_OF = { new: 0, contacted: 1, vob_pending: 2, vob_completed: 2, prescreen_done: 3, pending_admit: 4 };
const STEP_COMPLETE = { vob_completed: true, prescreen_done: true };
const ACT_LABELS = { call_logged: 'Call logged', note: 'Note' };

const isOverdue = (l) => l.next_action_at && new Date(l.next_action_at).getTime() <= Date.now();
const fmtDue = (t) => {
  if (!t) return '';
  const d = new Date(t); const now = new Date();
  if (d.getTime() <= now.getTime()) return 'Overdue ' + durShort(minsAgo(t));
  const endToday = new Date(now); endToday.setHours(23, 59, 59, 999);
  const endTom = new Date(endToday); endTom.setDate(endTom.getDate() + 1);
  if (d <= endToday) return 'Today ' + DOT + ' ' + timeAt(t);
  if (d <= endTom) return 'Tomorrow ' + DOT + ' ' + timeAt(t);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) + ' ' + timeAt(t);
};
const hoursToTomorrow8 = () => {
  const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0);
  return Math.max(1, (d.getTime() - Date.now()) / 3600000);
};

export default function Queue({ user }) {
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState(() => {
    const jump = localStorage.getItem('tcio_queue_rep');
    if (jump) { localStorage.removeItem('tcio_queue_rep'); return jump; }
    return 'mine';
  });
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [facFilter, setFacFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [snoozeOpen, setSnoozeOpen] = useState(null);
  const [nvOpen, setNvOpen] = useState(null);
  const [nvReason, setNvReason] = useState(NV_REASONS[0]);
  const [refModal, setRefModal] = useState(false);
  const [refPartners, setRefPartners] = useState([]);
  const [refForm, setRefForm] = useState({ partner: '', first: '', last: '', phone: '' });
  // ADD A LEAD SOURCE from the new-lead form (ticket bed45586, Chad 10/5)
  const addRefSource = async () => {
    const name = (window.prompt('Name of the new lead source:') || '').trim();
    if (name.length === 0) return;
    try {
      const r = await fetch(API + '/api/lead-sources', { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      const j = await r.json();
      if (j.ok === true && j.data) { setRefPartners(list => [{ ...j.data, kind: 'source' }, ...list.filter(x => (x.id === j.data.id) === false)]); setRefForm(f => ({ ...f, partner: j.data.id })); }
      else alert(j.error === 'Forbidden' ? 'You do not have permission to add a lead source.' : (j.error || 'Could not add the lead source.'));
    } catch (e) { alert('Could not add the lead source.'); }
  };
  const [facList, setFacList] = useState([]);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  const canViewOthers = ['ceo', 'admin', 'admissions'].includes(user?.role);
  const headers = { Authorization: 'Bearer ' + user.token };
  const jh = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  useEffect(() => {
    if (!canViewOthers) return;
    fetch(API + '/api/staff-list', { headers }).then(r => r.json()).then(j => {
      const ALLOW = ['andrew marquez', 'aurora carlsen', 'tiffini westfall', 'andre marquez', 'allison smith', 'jaclyn hicks'];
      if (j.ok) setReps((j.data || []).filter(u => u.active && ALLOW.includes(((u.first_name || '') + ' ' + (u.last_name || '')).toLowerCase().trim())));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(API + '/api/lookups', { headers }).then(r => r.json()).then(j => {
      if (!j.ok) return;
      const SKIP_SOURCES = ['Direct', 'Google Ads', 'Google My Business', 'Google Organic', 'Main Line', 'Meta Ads', 'Organic', 'Other', 'Website', 'Sales Rep / Team Member', 'Referral Partner'];
      const sources = (j.lead_sources || []).filter(x => !SKIP_SOURCES.includes(x.name))
        .sort((a, b) => (/journey/i.test(a.name) ? -1 : /journey/i.test(b.name) ? 1 : a.name.localeCompare(b.name)))
        .map(x => ({ ...x, kind: 'source' }));
      const partners = (j.referral_partners || []).map(x => ({ ...x, kind: 'partner' }));
      setRefPartners([...sources, ...partners]);
      if (sources[0]) setRefForm(f => ({ ...f, partner: sources[0].id }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(API + '/api/facilities', { headers }).then(r => r.json()).then(j => {
      const arr = Array.isArray(j) ? j : (j.data || j.facilities || []);
      setFacList(arr.map(f => ({ id: f.id, name: f.org_name || f.name })).filter(f => f.id && f.name));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const r = await fetch(API + '/api/queue?view=' + encodeURIComponent(view), { headers });
    const j = await r.json();
    if (j.ok) setQ(j);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [view]);

  const checkedFetch = async (url, opts, label) => {
    try {
      const r = await fetch(url, opts);
      if (r.status === 401) { alert('Your session expired \u2014 please log in again.'); return null; }
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) { alert(label + ' failed: ' + (d.error || r.status)); return null; }
      return d;
    } catch (e) { alert(label + ' failed: ' + e.message); return null; }
  };

  const logOutcome = async (leadId, outcome, note) => {
    setBusy(true);
    await checkedFetch(API + '/api/queue/' + leadId + '/outcome', { method: 'POST', headers: jh, body: JSON.stringify({ outcome, note }) }, 'Log outcome');
    setExpandedId(null); await load(); setBusy(false);
  };

  const dial = async (leadId, phone) => {
    setBusy(true);
    const r = await fetch(API + '/api/call/dial', { method: 'POST', headers: jh, body: JSON.stringify({ lead_id: leadId, phone }) });
    const j = await r.json();
    setBusy(false);
    if (j.ok) { alert(j.message); setExpandedId(leadId); await load(); }
    else if (j.error === 'no_phone') {
      const p = prompt('Enter your cell \u2014 CallRail will ring you first:');
      if (p) {
        const s2 = await fetch(API + '/api/me/phone', { method: 'PATCH', headers: jh, body: JSON.stringify({ phone: p }) });
        const sj = await s2.json();
        if (sj.ok) dial(leadId, phone); else alert(sj.error);
      }
    } else alert('Could not place call: ' + j.error);
  };

  const resolveCrisis = async (leadId) => {
    const note = prompt('Resolve crisis flag \u2014 what action was taken? (required)');
    if (!note || !note.trim()) return;
    setBusy(true);
    await checkedFetch(API + '/api/leads/' + leadId + '/crisis-resolve',
      { method: 'POST', headers: jh, body: JSON.stringify({ note: note.trim() }) }, 'Resolve crisis');
    await load(); setBusy(false);
  };

  const snooze = async (leadId, hours) => {
    setBusy(true);
    await checkedFetch(API + '/api/queue/' + leadId + '/snooze', { method: 'POST', headers: jh, body: JSON.stringify({ hours }) }, 'Snooze');
    setSnoozeOpen(null); setExpandedId(null); await load(); setBusy(false);
  };

  const markNotViable = async (leadId) => {
    await checkedFetch(API + '/api/leads/v2/' + leadId + '/stage', {
      method: 'PATCH', headers: jh,
      body: JSON.stringify({ stage: 'not_viable', lost_reason: 'Not viable: ' + nvReason })
    }, 'Mark not viable');
    setNvOpen(null); setNvReason(NV_REASONS[0]); setExpandedId(null); load();
  };

  if (selected) return <LeadDetail leadId={selected} user={user} onBack={() => { setSelected(null); load(); }} />;
  if (!q) return <div style={s.wrap}><div style={s.empty}>Loading{ELLIP}</div></div>;

  const working = [...(q.now || []), ...(q.today || [])].map(l => ({ ...l, _p: prioritize(l, (q.now || []).some(x => x.id === l.id) ? 'now' : 'today') }))
    .sort((a, b) => a._p.tier - b._p.tier || b._p.score - a._p.score);
  const future = [...(q.tomorrow || []), ...(q.later || [])].map(l => ({ ...l, _p: prioritize(l, 'later') }));
  const allRows = [...working, ...future];
  const nurture = q.nurture || [];

  const facilities = facList;

  const matchSearch = (l) => {
    if (!search) return true;
    const qq = search.toLowerCase();
    const ph = (l.phone || '').replace(/\D/g, '');
    return ((l.first_name || '') + ' ' + (l.last_name || '')).toLowerCase().includes(qq)
      || ph.includes(qq.replace(/\D/g, '') || '~~')
      || (l.payer_name || '').toLowerCase().includes(qq)
      || (l.primary_substance || '').toLowerCase().includes(qq)
      || (TASK_LABELS[taskOf(l)] || '').toLowerCase().includes(qq);
  };
  const matchFac = (l) => facFilter === 'all' || l.facility_id === facFilter;
  const matchDue = (l) => {
    if (dueFilter === 'all') return true;
    if (!l.next_action_at) return false;
    const d = new Date(l.next_action_at); const now = new Date();
    const endToday = new Date(now); endToday.setHours(23, 59, 59, 999);
    const endTom = new Date(endToday); endTom.setDate(endTom.getDate() + 1);
    const endWeek = new Date(endToday); endWeek.setDate(endWeek.getDate() + 7);
    if (dueFilter === 'overdue') return d <= now;
    if (dueFilter === 'today') return d <= endToday;
    if (dueFilter === 'tomorrow') return d > endToday && d <= endTom;
    if (dueFilter === 'week') return d <= endWeek;
    return true;
  };

  const base = allRows.filter(l => matchSearch(l) && matchFac(l) && matchDue(l));
  const cnt = { call: 0, callback: 0, followup: 0, vob: 0, prescreen: 0, admit: 0 };
  const cntOver = { call: 0, callback: 0, followup: 0, vob: 0, prescreen: 0, admit: 0 };
  let overdueTotal = 0;
  base.forEach(l => {
    const t = taskOf(l); cnt[t] += 1;
    if (isOverdue(l)) { cntOver[t] += 1; overdueTotal += 1; }
  });
  const workingOverdue = working.filter(isOverdue).length;
  const dueTodayFresh = working.length - workingOverdue;

  const TABS = [
    ['all', 'All Tasks', base.length],
    ['overdue', 'Overdue', overdueTotal],
    ['calls', 'Calls', cnt.call],
    ['followups', 'Follow Ups', cnt.followup + cnt.callback],
    ['vobs', 'VOBs', cnt.vob],
    ['prescreens', 'Prescreens', cnt.prescreen],
    ['admits', 'Admits', cnt.admit],
  ];
  const matchTab = (l) => {
    if (tab === 'all') return true;
    if (tab === 'overdue') return isOverdue(l);
    if (tab === 'calls') return taskOf(l) === 'call';
    if (tab === 'followups') return ['followup', 'callback'].includes(taskOf(l));
    if (tab === 'vobs') return taskOf(l) === 'vob';
    if (tab === 'prescreens') return taskOf(l) === 'prescreen';
    if (tab === 'admits') return taskOf(l) === 'admit';
    return true;
  };
  const visible = base.filter(matchTab);
  const spotlight = (tab === 'all' && dueFilter === 'all' && !search) ? visible.find(r => r._p.tier <= 5) : null;
  const rows = spotlight ? visible.filter(r => r.id !== spotlight.id) : visible;
  const nurtureShown = nurture.filter(l => matchSearch(l) && matchFac(l));

  const tiles = [
    { key: 'calls', I: PhoneCall, ac: tk.blue600, tint: tk.aiSoft, n: cnt.call, label: 'Calls', sub: cntOver.call ? cntOver.call + ' overdue' : '' },
    { key: 'followups', I: RefreshCcw, ac: tk.purple, tint: tk.purpleSoft, n: cnt.followup + cnt.callback, label: 'Follow Ups', sub: (cntOver.followup + cntOver.callback) ? (cntOver.followup + cntOver.callback) + ' overdue' : '' },
    { key: 'vobs', I: FileCheck2, ac: tk.teal, tint: tk.tealSoft, n: cnt.vob, label: 'VOBs', sub: cntOver.vob ? cntOver.vob + ' overdue' : '' },
    { key: 'prescreens', I: ClipboardCheck, ac: tk.purple, tint: tk.purpleSoft, n: cnt.prescreen, label: 'Prescreens', sub: cntOver.prescreen ? cntOver.prescreen + ' overdue' : '' },
    { key: 'admits', I: CalendarClock, ac: tk.success, tint: tk.successBg, n: cnt.admit, label: 'Admits to Confirm', sub: cntOver.admit ? cntOver.admit + ' overdue' : '' },
  ];

  const SnoozeMenu = (l) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
      <button disabled={busy} onClick={(e) => { e.stopPropagation(); snooze(l.id, 0.5); }} className="tqSeg" style={s.seg}>30 min</button>
      <button disabled={busy} onClick={(e) => { e.stopPropagation(); snooze(l.id, 1); }} className="tqSeg" style={s.seg}>1 hour</button>
      <button disabled={busy} onClick={(e) => { e.stopPropagation(); snooze(l.id, hoursToTomorrow8()); }} className="tqSeg" style={s.seg}>Tomorrow 8 AM</button>
      <button disabled={busy} onClick={(e) => { e.stopPropagation(); const h = parseFloat(prompt('Snooze for how many hours?') || ''); if (h > 0) snooze(l.id, h); }} className="tqSeg" style={s.seg}>Custom{ELLIP}</button>
      <button onClick={(e) => { e.stopPropagation(); setSnoozeOpen(null); }} style={s.btnGhost}>Cancel</button>
    </div>
  );

  const Expanded = (l) => {
    const g = l.actual_collected_rate ? payerGrade(l.actual_collected_rate) : null;
    const cur = STEP_OF[l.pipeline_stage] != null ? STEP_OF[l.pipeline_stage] : 0;
    const curDone = !!STEP_COMPLETE[l.pipeline_stage];
    const attempts = l.contact_attempts || 0;
    const over = isOverdue(l);
    return (
      <div style={s.expandWrap} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px 0' }}>
          <span style={{ ...s.avatar, width: 22, height: 22, fontSize: 9, background: avColor(l.first_name) }}>{initials(l.first_name, l.last_name)}</span>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: tk.ink }}>{l.first_name} {l.last_name}</span>
          <span style={{ fontSize: 12, color: tk.textMuted }}>{TIER_NAMES[l._p.tier]} {DOT} {l._p.why}</span>
        </div>
        <div style={{ ...s.expand, gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr 1.5fr 1.4fr' }}>
          <div>
            <div style={s.secTitle}>LEAD SNAPSHOT</div>
            <div style={s.kv}><span style={s.k}>Phone</span><span style={s.v}>{l.phone || '\u2014'}</span></div>
            {l.source_channel && <div style={s.kv}><span style={s.k}>Source</span><span style={s.v}>{l.source_channel}</span></div>}
            {l.primary_substance && <div style={s.kv}><span style={s.k}>Substance</span><span style={s.v}>{l.primary_substance}</span></div>}
            {l.how_soon && <div style={s.kv}><span style={s.k}>Wants in</span><span style={s.v}>{l.how_soon}</span></div>}
            {l.payer_name && <div style={s.kv}><span style={s.k}>Insurance</span><span style={s.v}>{l.payer_name}{g && <span style={{ ...s.grade, background: g.c }}>{g.t}</span>}</span></div>}
            {l.facility_name && <div style={s.kv}><span style={s.k}>Facility</span><span style={s.v}>{l.facility_name}</span></div>}
            {l.avg_episode_value && <div style={s.kv}><span style={s.k}>Episode</span><span style={s.v}>{money(l.avg_episode_value)}{l.rate_source === 'company' ? ' (company avg)' : ''}</span></div>}
          </div>
          <div>
            <div style={s.secTitle}>NEXT ACTION</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink, letterSpacing: '-0.01em' }}>{TASK_LABELS[taskOf(l)]}</div>
            <div style={{ fontSize: 13, color: over ? tk.danger : tk.textSecondary, fontWeight: over ? 700 : 500, marginTop: 2 }}>{fmtDue(l.next_action_at)}</div>
            <div style={{ fontSize: 12.5, color: tk.textSecondary, marginTop: 5 }}>{l._p.why}</div>
            {attempts > 0 && <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 6, color: attempts >= 4 ? tk.danger : attempts >= 3 ? tk.warning : tk.textMuted }}>Attempt {Math.min(attempts + 1, 5)} of 5{attempts >= 4 ? ' \u2014 last one before nurture' : ''}</div>}
            <div style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 6px' }}>Log outcome</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <button disabled={busy} onClick={() => logOutcome(l.id, 'answered')} className="tqSeg" style={{ ...s.seg, color: tk.success, borderColor: tk.success }}>Talked</button>
              <button disabled={busy} onClick={() => logOutcome(l.id, 'no_answer')} className="tqSeg" style={s.seg}>No answer</button>
              <button disabled={busy} onClick={() => logOutcome(l.id, 'voicemail')} className="tqSeg" style={s.seg}>Voicemail</button>
              <button disabled={busy} onClick={() => { const n = prompt('Note (optional):'); logOutcome(l.id, 'answered', n); }} className="tqSeg" style={s.seg}>+ Note</button>
            </div>
            {snoozeOpen === l.id && SnoozeMenu(l)}
            {nvOpen === l.id && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <select value={nvReason} onChange={e => setNvReason(e.target.value)} style={s.select}>
                  {NV_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button disabled={busy} onClick={() => markNotViable(l.id)} style={s.nvConfirm}>Confirm</button>
                <button onClick={() => setNvOpen(null)} style={s.btnGhost}>Cancel</button>
              </div>
            )}
          </div>
          <div>
            <div style={s.secTitle}>ADMISSION PROGRESS</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 6, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              {SPINE.map((st, i) => {
                const done = i < cur || (i === cur && curDone);
                const current = i === cur;
                return (
                  <div key={st} style={{ display: 'flex', alignItems: 'center', flex: i < SPINE.length - 1 ? 1 : 'none' }}>
                    <div style={{ textAlign: 'center', minWidth: 52 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 11, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                        background: done ? tk.success : current ? tk.ai : tk.surfaceSoft,
                        color: done || current ? '#FFFFFF' : tk.textMuted,
                        border: '2px solid ' + (current ? tk.ai : done ? tk.success : tk.border),
                        boxShadow: current ? '0 0 0 3px ' + tk.aiSoft : 'none'
                      }}>{done ? CHECK : i + 1}</div>
                      <div style={{ fontSize: 10, fontWeight: current ? 700 : 600, marginTop: 5, color: current ? tk.ai : done ? tk.ink : tk.textMuted }}>{st}</div>
                    </div>
                    {i < SPINE.length - 1 && <div style={{ flex: 1, height: 2, background: i < cur ? tk.teal : tk.borderSoft, margin: '0 2px 14px' }} />}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 8 }}>Current stage: <span style={{ fontWeight: 700, color: tk.ai }}>{STAGE_LABELS[l.pipeline_stage] || l.pipeline_stage}</span></div>
          </div>
          <div>
            <div style={s.secTitle}>RECENT ACTIVITY</div>
            {l.last_activity_ts ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: tk.ink }}>{ACT_LABELS[l.last_activity_type] || l.last_activity_type || 'Activity'}</div>
                {l.last_note && <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 2 }}>{trunc(l.last_note, 110)}</div>}
                <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 2 }}>{timeAgo(l.last_activity_ts)}</div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: tk.textMuted }}>No activity logged yet.</div>
            )}
            <button onClick={() => setSelected(l.id)} className="tqFix" style={{ ...s.linkBtn, marginTop: 8 }}>View full activity <ArrowRight size={11} style={{ verticalAlign: '-1px' }} /></button>
          </div>
        </div>
        <div style={s.actionBar}>
          <button disabled={busy} onClick={() => dial(l.id, l.phone)} style={s.btnCall}><Phone size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Call Now</button>
          <button onClick={() => setSelected(l.id)} style={s.btnBlue}>Open Lead</button>
          <button disabled={busy} onClick={() => setSnoozeOpen(snoozeOpen === l.id ? null : l.id)} style={s.btnGhost}>Snooze</button>
          {l.crisis_flag && !l.crisis_resolved_at &&
            <button onClick={() => resolveCrisis(l.id)} disabled={busy} style={{ ...s.btnGhost, background: tk.dangerBg, color: tk.danger, borderColor: tk.danger, fontWeight: 700 }}>Resolve crisis</button>}
          {nvOpen !== l.id && <button disabled={busy} onClick={() => { setNvOpen(l.id); setNvReason(NV_REASONS[0]); }} style={{ ...s.btnGhost, color: tk.danger, borderColor: tk.dangerBg, marginLeft: 'auto' }}>Not viable</button>}
        </div>
      </div>
    );
  };

  const RowD = (l) => {
    const p = prioOf(l._p.tier);
    const over = isOverdue(l);
    const open = expandedId === l.id;
    const smart = l._p.tier <= 4;
    return (
      <div key={l.id}>
        <div onClick={() => setExpandedId(open ? null : l.id)} className={'tqRow' + (open ? ' tqRowOpen' : '')}
          style={{ ...s.row, gridTemplateColumns: s.gridCols, borderLeft: over ? '3px solid ' + tk.danger : '3px solid transparent' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 8, color: p.c }}>{BULLET}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: tk.ink, letterSpacing: '0.04em' }}>{p.label}</span>
              {smart && <span title={'TCIO Priority: ' + TIER_NAMES[l._p.tier] + ' \u2014 ' + l._p.why} style={{ color: tk.purple, fontSize: 11, cursor: 'default' }}>{STAR}</span>}
            </div>
            {over && <div style={{ fontSize: 11, fontWeight: 600, color: tk.danger, marginTop: 2 }}>Overdue {durShort(minsAgo(l.next_action_at))}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <span style={{ ...s.avatar, background: avColor(l.first_name) }}>{initials(l.first_name, l.last_name)}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.first_name} {l.last_name}</div>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>{l.payer_name || l.phone || ''}</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: tk.ink }}>{TASK_LABELS[taskOf(l)]}</div>
            <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 1 }}>{TIER_NAMES[l._p.tier]}{l.contact_attempts > 0 ? ' ' + DOT + ' attempt ' + Math.min(l.contact_attempts + 1, 5) + ' of 5' : ''}</div>
          </div>
          <div style={{ fontSize: 12.5, color: over ? tk.danger : tk.textSecondary, fontWeight: over ? 600 : 500 }}>{fmtDue(l.next_action_at)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: stageDot(l.pipeline_stage), flex: 'none' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: tk.textSecondary }}>{STAGE_LABELS[l.pipeline_stage] || l.pipeline_stage}</span>
          </div>
          <div>
            {l.rep_first ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ ...s.avatar, width: 20, height: 20, fontSize: 8.5, background: avColor(l.rep_first) }}>{initials(l.rep_first, l.rep_last)}</span>
                <span style={{ fontSize: 12, color: tk.textSecondary }}>{l.rep_first} {(l.rep_last || ' ')[0]}.</span>
              </div>
            ) : (
              <span style={{ fontSize: 11, fontWeight: 700, color: tk.warning }}>Unclaimed {DOT} first to call</span>
            )}
          </div>
          <div>
            {l.last_activity_ts ? (
              <div>
                <div style={{ fontSize: 12, color: tk.textSecondary }}>{trunc(l.last_note || ACT_LABELS[l.last_activity_type] || l.last_activity_type || '', 34)}</div>
                <div style={{ fontSize: 11, color: tk.textMuted }}>{timeAgo(l.last_activity_ts)}</div>
              </div>
            ) : <span style={{ fontSize: 12, color: tk.textMuted }}>{'\u2014'}</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <button disabled={busy} onClick={() => dial(l.id, l.phone)} style={s.btnCallSm}>Call</button>
            <ChevronDown size={15} color={tk.textMuted} className={'tqChev' + (open ? ' tqChevOpen' : '')} style={{ cursor: 'pointer' }} onClick={() => setExpandedId(open ? null : l.id)} />
          </div>
        </div>
        {open && Expanded(l)}
      </div>
    );
  };

  const RowM = (l) => {
    const p = prioOf(l._p.tier);
    const over = isOverdue(l);
    const open = expandedId === l.id;
    return (
      <div key={l.id} style={{ ...s.mcard, borderLeft: over ? '3px solid ' + tk.danger : '3px solid ' + tk.border }}>
        <div onClick={() => setExpandedId(open ? null : l.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: p.c }}>{BULLET} {p.label}</span>
            <span style={{ fontSize: 12, fontWeight: over ? 700 : 500, color: over ? tk.danger : tk.textSecondary }}>{fmtDue(l.next_action_at)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
            <span style={{ ...s.avatar, background: avColor(l.first_name) }}>{initials(l.first_name, l.last_name)}</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: tk.ink }}>{l.first_name} {l.last_name}</div>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>{l.payer_name || l.phone || ''}{!l.rep_first ? ' ' + DOT + ' Unclaimed' : ''}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: tk.ink, marginTop: 6 }}>{TASK_LABELS[taskOf(l)]} <span style={{ fontWeight: 400, color: tk.textMuted }}>{DOT} {TIER_NAMES[l._p.tier]}</span></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button disabled={busy} onClick={() => dial(l.id, l.phone)} style={{ ...s.btnCall, flex: 1 }}>Call</button>
          <button onClick={() => setExpandedId(open ? null : l.id)} style={s.btnGhost}>{open ? 'Close' : 'Details'}</button>
        </div>
        {open && Expanded(l)}
      </div>
    );
  };

  return (
    <div style={s.wrap}>
      <style>{CSS}</style>
      <div style={{ ...s.head, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={s.h1}>My Queue</h1>
          <p style={s.sub}>{working.length > 0 ? (dueTodayFresh > 0 ? dueTodayFresh + ' due today ' + DOT + ' ' : '') + workingOverdue + ' overdue' : 'Nothing due right now. Nice work.'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setRefModal(true)} style={s.btnBlue}>+ Log Referral</button>
          {facilities.length > 1 && (
            <select value={facFilter} onChange={e => setFacFilter(e.target.value)} style={s.select}>
              <option value="all">All facilities</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          {canViewOthers && (
            <select value={view} onChange={e => setView(e.target.value)} style={s.select}>
              <option value="mine">My queue</option>
              <option value="all">Everyone</option>
              <option value="unclaimed">Unclaimed only</option>
              <optgroup label="By rep">
                {reps.map(r => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
              </optgroup>
            </select>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 12, marginBottom: 14 }}>
        {tiles.map(t => {
          const I = t.I;
          return (
            <div key={t.key} onClick={() => setTab(tab === t.key ? 'all' : t.key)}
              style={{ ...s.tile, borderColor: tab === t.key ? tk.ai : tk.border, background: tab === t.key ? tk.aiSoft : tk.surface }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <I size={18} color={t.ac} strokeWidth={1.75} />
                <span style={s.tileLabel}>{t.label}</span>
              </div>
              <span style={{ fontSize: 21, fontWeight: 700, color: t.ac, letterSpacing: '-0.02em' }}>{t.n}</span>
              {t.sub && <span style={{ fontSize: 11, fontWeight: 600, color: tk.danger }}>{t.sub}</span>}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={'Search your queue \u2014 name, phone, payer, or task' + ELLIP}
            style={s.search} />
          {search && <button onClick={() => setSearch('')} style={s.searchX}>{TIMES}</button>}
        </div>
        <select value={dueFilter} onChange={e => setDueFilter(e.target.value)} style={s.select}>
          <option value="all">Due: Any time</option>
          <option value="overdue">Overdue</option>
          <option value="today">Today</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="week">This week</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 14, flexWrap: 'wrap', borderBottom: '1px solid ' + tk.borderSoft }}>
        {TABS.map(([k, label, n]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ ...s.tab, color: tab === k ? tk.ai : tk.textSecondary, borderBottom: tab === k ? '2px solid ' + tk.ai : '2px solid transparent', fontWeight: tab === k ? 700 : 500 }}>
            {label}{n > 0 ? ' ' + n : ''}
          </button>
        ))}
      </div>

      {q.total === 0 && (
        <div style={s.empty}><div style={{ fontSize: 34, marginBottom: 8, color: tk.success }}>{CHECK}</div>Queue is clear.</div>
      )}

      {spotlight && (
        <div style={s.nextUp}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={s.nextUpLabel}><Sparkles size={15} color={tk.purple} strokeWidth={1.75} /> TCIO NEXT UP</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 4 }}>
              <span style={{ ...s.avatar, background: avColor(spotlight.first_name) }}>{initials(spotlight.first_name, spotlight.last_name)}</span>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: tk.ink, letterSpacing: '-0.01em' }}>{spotlight.first_name} {spotlight.last_name}
                  <span style={{ fontSize: 13, fontWeight: 500, color: tk.textSecondary, marginLeft: 8 }}>{spotlight.payer_name || ''}</span>
                </div>
                <div style={{ fontSize: 13, color: tk.textSecondary, marginTop: 2 }}>{spotlight._p.why}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ ...s.miniChip, color: prioOf(spotlight._p.tier).c, borderColor: prioOf(spotlight._p.tier).c }}>{prioOf(spotlight._p.tier).label}</span>
              {isOverdue(spotlight) && <span style={{ ...s.miniChip, color: tk.danger, borderColor: tk.danger }}>Overdue {durShort(minsAgo(spotlight.next_action_at))}</span>}
              {spotlight.contact_attempts > 0 && <span style={{ ...s.miniChip, color: tk.textSecondary, borderColor: tk.border }}>Attempt {Math.min(spotlight.contact_attempts + 1, 5)} of 5</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button disabled={busy} onClick={() => dial(spotlight.id, spotlight.phone)} style={{ ...s.btnCall, fontSize: 14, padding: '11px 22px' }}>Call Now</button>
            <ChevronDown size={16} color={tk.textMuted} className={'tqChev' + (expandedId === spotlight.id ? ' tqChevOpen' : '')} style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === spotlight.id ? null : spotlight.id)} />
          </div>
        </div>
      )}
      {spotlight && expandedId === spotlight.id && Expanded(spotlight)}

      {!isMobile ? (
        <div style={s.table}>
          <div style={{ ...s.thead, gridTemplateColumns: s.gridCols }}>
            <div>Priority</div><div>Lead / Patient</div><div>Task</div><div>Due</div><div>Stage</div><div>Owner</div><div>Last Activity</div><div style={{ textAlign: 'right' }}>Action</div>
          </div>
          {rows.map(RowD)}
          {rows.length === 0 && q.total > 0 && <div style={{ ...s.empty, padding: 30 }}>No tasks in this view.</div>}
        </div>
      ) : (
        <div>
          {rows.map(RowM)}
          {rows.length === 0 && q.total > 0 && <div style={{ ...s.empty, padding: 30 }}>No tasks in this view.</div>}
        </div>
      )}

      {nurtureShown.length > 0 && (
        <div style={{ opacity: 0.6, marginTop: 20 }}>
          <div style={s.nurtureHead}>NURTURE {DOT} {nurtureShown.length}</div>
          {nurtureShown.map(l => (
            <div key={l.id} style={s.nrow} onClick={() => setSelected(l.id)}>
              <span style={{ fontSize: 13, fontWeight: 600, color: tk.ink }}>{l.first_name} {l.last_name}</span>
              <span style={{ fontSize: 12, color: tk.textMuted }}> {DOT} {l.contact_attempts} attempts {DOT} next touch {new Date(l.next_action_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</span>
            </div>
          ))}
        </div>
      )}

      {refModal && (
        <div style={s.modalBg} onClick={() => setRefModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 17, color: tk.blue700, marginBottom: 4 }}>Log a Referral</div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginBottom: 14 }}>Creates the lead instantly {'\u2014'} then call through TCIO.</div>
            <select value={refForm.partner} onChange={e => { if (e.target.value === '__add_source') { addRefSource(); return; } setRefForm({ ...refForm, partner: e.target.value }); }} style={{ ...s.input, marginBottom: 10 }}>
              <optgroup label="Referral Sources">
                {refPartners.filter(p => p.kind === 'source').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                <option value="__add_source">+ Add a new lead source…</option>
              </optgroup>
              <optgroup label="Treatment Center Partners">
                {refPartners.filter(p => p.kind === 'partner').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            </select>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input placeholder="First name" value={refForm.first} onChange={e => setRefForm({ ...refForm, first: e.target.value })} style={{ ...s.input, flex: 1 }} />
              <input placeholder="Last name" value={refForm.last} onChange={e => setRefForm({ ...refForm, last: e.target.value })} style={{ ...s.input, flex: 1 }} />
            </div>
            <input placeholder="Phone" value={refForm.phone} onChange={e => setRefForm({ ...refForm, phone: e.target.value })} style={{ ...s.input, width: '100%', marginBottom: 14, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => setRefModal(false)} style={s.btnGhost}>Cancel</button>
              <button disabled={busy || !refForm.first.trim() || !refForm.phone.trim()}
                onClick={async () => {
                  const p = refPartners.find(x => x.id === refForm.partner);
                  const body = { first_name: refForm.first, last_name: refForm.last, phone: refForm.phone, partner_name: p ? p.name : null };
                  if (p && p.kind === 'source') body.lead_source_id = p.id; else body.referral_partner_id = refForm.partner || null;
                  const r = await fetch(API + '/api/referrals/log-and-create', { method: 'POST', headers: jh, body: JSON.stringify(body) });
                  const j = await r.json();
                  if (j.ok) { setRefModal(false); setRefForm({ partner: refForm.partner, first: '', last: '', phone: '' }); load(); }
                  else alert('Failed: ' + j.error);
                }}
                style={s.btnBlue}>Create Lead</button>
              <button disabled={busy || !refForm.first.trim() || !refForm.phone.trim()}
                onClick={async () => {
                  const p = refPartners.find(x => x.id === refForm.partner);
                  const body = { first_name: refForm.first, last_name: refForm.last, phone: refForm.phone, partner_name: p ? p.name : null };
                  if (p && p.kind === 'source') body.lead_source_id = p.id; else body.referral_partner_id = refForm.partner || null;
                  const r = await fetch(API + '/api/referrals/log-and-create', { method: 'POST', headers: jh, body: JSON.stringify(body) });
                  const j = await r.json();
                  if (j.ok) {
                    setRefModal(false);
                    const ph = refForm.phone;
                    setRefForm({ partner: refForm.partner, first: '', last: '', phone: '' });
                    await dial(j.lead_id, ph);
                    load();
                  } else alert('Failed: ' + j.error);
                }}
                style={s.btnCall}>Create + Call</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  gridCols: '96px 1.5fr 1.15fr 1fr 1.1fr 1fr 1.3fr 92px',
  wrap: { padding: '22px 26px', maxWidth: 1320, margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh', boxSizing: 'border-box' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  h1: { fontSize: 25, fontWeight: 700, color: tk.ink, margin: 0, letterSpacing: '-0.02em' },
  sub: { color: tk.textSecondary, margin: '4px 0 0', fontSize: 13.5 },
  tile: { display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', borderRadius: tk.radiusCard, border: '1px solid ' + tk.border, cursor: 'pointer', boxShadow: tk.shadow, transition: 'border-color 160ms ease, background 160ms ease' },
  tileIcon: { width: 26, height: 26, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
  tileLabel: { fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' },
  search: { width: '100%', padding: '10px 12px', border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, fontSize: 14, boxSizing: 'border-box', background: tk.surface, color: tk.ink, fontFamily: tk.font },
  searchX: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: tk.textMuted, fontSize: 14 },
  select: { fontSize: 13, fontWeight: 600, padding: '9px 10px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, background: tk.surface, color: tk.ink, cursor: 'pointer', fontFamily: tk.font },
  tab: { background: 'none', border: 'none', padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: tk.font },
  nextUp: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'linear-gradient(95deg, ' + tk.aiSoft + ', ' + tk.surface + ' 55%)', border: '1px solid ' + tk.border, borderLeft: '3px solid ' + tk.purple, borderRadius: tk.radiusCard, padding: '14px 18px', marginBottom: 16, boxShadow: tk.shadow },
  nextUpLabel: { fontSize: 11, fontWeight: 700, color: tk.ink, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6 },
  miniChip: { fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 9, border: '1px solid', background: tk.surface, letterSpacing: '0.03em' },
  table: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, overflow: 'hidden', boxShadow: tk.shadow },
  thead: { display: 'grid', gap: 10, padding: '8px 14px', background: tk.surfaceSoft, borderBottom: '1px solid ' + tk.border, fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'grid', gap: 10, padding: '12px 14px', borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer', alignItems: 'center' },
  avatar: { width: 28, height: 28, borderRadius: 14, color: '#FFFFFF', fontSize: 10.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', textTransform: 'uppercase' },
  expandWrap: { background: tk.surfaceSoft, borderBottom: '1px solid ' + tk.border, borderLeft: '3px solid ' + tk.ai },
  expand: { display: 'grid', gap: 20, padding: '12px 18px 14px' },
  actionBar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 18px 14px', borderTop: '1px solid ' + tk.borderSoft },
  secTitle: { fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 },
  kv: { display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 },
  k: { color: tk.textMuted, minWidth: 66, flex: 'none' },
  v: { color: tk.ink, fontWeight: 500 },
  grade: { color: '#FFFFFF', fontSize: 9, fontWeight: 700, borderRadius: 4, padding: '1px 5px', marginLeft: 6 },
  mcard: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: 14, marginBottom: 10, boxShadow: tk.shadow },
  nurtureHead: { fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  nrow: { background: tk.surface, border: '1px solid ' + tk.borderSoft, borderRadius: tk.radiusControl, padding: '9px 14px', marginBottom: 6, cursor: 'pointer' },
  btnCall: { background: tk.ai, color: '#FFFFFF', border: 'none', borderRadius: tk.radiusControl, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: tk.font },
  btnCallSm: { background: tk.ai, color: '#FFFFFF', border: 'none', borderRadius: tk.radiusControl, padding: '7px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: tk.font },
  btnBlue: { background: tk.surface, color: tk.blue700, border: '1px solid ' + tk.blue600, borderRadius: tk.radiusControl, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: tk.font },
  btnGhost: { background: 'none', color: tk.textSecondary, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: tk.font },
  seg: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: tk.textSecondary, fontFamily: tk.font },
  linkBtn: { background: 'none', border: 'none', color: tk.ai, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: tk.font },
  nvConfirm: { fontSize: 13, padding: '7px 14px', borderRadius: tk.radiusControl, border: 'none', background: tk.danger, color: '#FFFFFF', fontWeight: 700, cursor: 'pointer', fontFamily: tk.font },
  input: { padding: '9px 10px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, fontSize: 14, fontFamily: tk.font, color: tk.ink, background: tk.surface },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(10, 34, 57, 0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: tk.surface, borderRadius: tk.radiusCard, padding: 22, width: '100%', maxWidth: 440, boxShadow: tk.shadow },
  empty: { padding: 60, textAlign: 'center', color: tk.textMuted, fontSize: 14 },
};
